import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, MapPin, Clock } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { getLiveStats, type LiveStats, type RecentLead } from "@/lib/live-stats.functions";

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just nu";
  if (min < 60) return `för ${min} min sedan`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `för ${hr} ${hr === 1 ? "timme" : "timmar"} sedan`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "i går";
  if (day < 7) return `för ${day} dagar sedan`;
  const wk = Math.floor(day / 7);
  return `för ${wk} ${wk === 1 ? "vecka" : "veckor"} sedan`;
}

function placeFromSource(source: string | null): string | null {
  if (!source) return null;
  // /solfilm-malmo → "Malmö", /solfilm-foretag → null
  const m = source.match(/solfilm-(malmo|lund|helsingborg|goteborg)/i);
  if (!m) return null;
  const map: Record<string, string> = {
    malmo: "Malmö",
    lund: "Lund",
    helsingborg: "Helsingborg",
    goteborg: "Göteborg",
  };
  return map[m[1].toLowerCase()] ?? null;
}

export function LiveTrustTicker() {
  const [mounted, setMounted] = useState(false);
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [idx, setIdx] = useState(0);
  const fetchStats = useServerFn(getLiveStats);

  useEffect(() => {
    setMounted(true);
    let cancelled = false;
    fetchStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch(() => {
        if (!cancelled) setStats({ today: 0, last7d: 0, recent: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [fetchStats]);

  useEffect(() => {
    if (!stats || stats.recent.length === 0) return;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % stats.recent.length),
      4200,
    );
    return () => clearInterval(t);
  }, [stats]);

  if (!mounted || !stats) {
    return <div className="bg-navy border-y border-gold/20 h-[44px]" aria-hidden />;
  }

  const cur: RecentLead | undefined = stats.recent[idx];
  const place = cur ? placeFromSource(cur.source_page) : null;

  // No real activity yet → show 7-day rollup instead of fake "today"
  const showToday = stats.today > 0;
  const headline = showToday
    ? `${stats.today} ${stats.today === 1 ? "offert skickad" : "offerter skickade"} idag`
    : stats.last7d > 0
      ? `${stats.last7d} ${stats.last7d === 1 ? "offert" : "offerter"} senaste 7 dagarna`
      : "Senaste aktivitet";

  return (
    <div className="bg-navy border-y border-gold/20">
      <div className="mx-auto max-w-7xl px-3 md:px-8 lg:px-10 py-1.5 md:py-2.5 lg:py-3 flex items-center justify-center gap-x-3 md:gap-x-4 lg:gap-x-6 text-[10px] md:text-[11px] lg:text-xs font-mono uppercase tracking-[0.06em] md:tracking-[0.12em] lg:tracking-[0.18em] text-center whitespace-nowrap overflow-hidden">
        <div className="flex items-center gap-1.5 md:gap-2 text-gold-bright min-w-0">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-teal-bright opacity-70 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-bright" />
          </span>
          <Activity size={12} className="shrink-0" />
          <span className="truncate">{headline}</span>
        </div>
        {cur && (
          <>
            <div className="hidden lg:block h-3 w-px bg-cream/15" />
            <div className="hidden lg:flex items-center gap-2 text-cream/70 min-h-[20px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span
                  key={idx}
                  initial={{ y: 14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -14, opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center justify-center gap-2 flex-wrap"
                >
                  <MapPin size={11} className="text-gold/70 shrink-0" />
                  <span className="text-cream/90 whitespace-nowrap">
                    Senaste offert{place ? `: ${place}` : ""}
                  </span>
                  <span className="text-gold/60">·</span>
                  <span className="whitespace-nowrap">{cur.product}</span>
                  <span className="text-gold/60">·</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Clock size={11} className="text-gold/70 shrink-0" />
                    {timeAgo(cur.created_at)}
                  </span>
                </motion.span>
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

